import { Box, CircularProgress } from "@outschool/backpack";
import { dayjs } from "@outschool/time";
import { gql, useMutation, useQuery } from "@outschool/ui-apollo";
import React, { useEffect, useRef } from "react";

import {
  BlockScopedThreadDetailQuery,
  BlockScopedThreadDetailQueryVariables,
} from "../../generated/graphql";
import { MARK_THREAD_AS_READ_MUTATION } from "../../mutations/MarkThreadAsRead";
import { BLOCK_SCOPED_THREAD_DETAIL_QUERY } from "../../queries/BlockScopedThreadDetailQuery";
import { MESSAGE_TEACHER_POLL_INTERVAL_MS } from "./constants";
import DateSeparator from "./DateSeparator";
import MessageBubble from "./MessageBubble";
import MessageInput from "./MessageInput";
import ThreadViewLayout from "./ThreadViewLayout";
import { Leader, LearnerInfo, MessageData, SectionInfo } from "./types";

function isSameGroup(a: MessageData, b: MessageData): boolean {
  return (
    a.sender.uid === b.sender.uid &&
    dayjs(a.sentAt).format("YYYY-MM-DD HH:mm") ===
      dayjs(b.sentAt).format("YYYY-MM-DD HH:mm")
  );
}

interface ThreadDetailViewProps {
  threadUid: string;
  section: SectionInfo;
  learner: LearnerInfo;
  leader: Leader | null;
  postTitle?: string | null;
  onBack: () => void;
  onClose: () => void;
}

export default function ThreadDetailView({
  threadUid,
  section,
  learner,
  leader,
  postTitle: postTitleProp,
  onBack,
  onClose,
}: ThreadDetailViewProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [markAsRead] = useMutation(MARK_THREAD_AS_READ_MUTATION, {
    update(cache) {
      const threadCacheId = cache.identify({
        __typename: "BlockScopedThread",
        uid: threadUid,
      });
      const prev = cache.readFragment<{ unreadCount: number }>({
        id: threadCacheId,
        fragment: gql`
          fragment ThreadUnread on BlockScopedThread {
            unreadCount
          }
        `,
      });
      const previousUnread = prev?.unreadCount ?? 0;
      cache.modify({
        id: threadCacheId,
        fields: { unreadCount: () => 0 },
      });
      const sectionCacheId = cache.identify({
        __typename: "Section",
        uid: section.uid,
      });
      cache.modify({
        id: sectionCacheId,
        fields: {
          unreadBlockScopedThreadsCount(existing: number = 0) {
            return Math.max(0, existing - previousUnread);
          },
        },
      });
    },
  });

  const { data, loading, refetch } = useQuery<
    BlockScopedThreadDetailQuery,
    BlockScopedThreadDetailQueryVariables
  >(BLOCK_SCOPED_THREAD_DETAIL_QUERY, {
    variables: { threadUid },
    fetchPolicy: "cache-and-network",
    pollInterval: MESSAGE_TEACHER_POLL_INTERVAL_MS,
    onCompleted: () => {
      markAsRead({ variables: { threadUid } });
    },
  });

  const thread = data?.blockScopedThread;
  const messages: MessageData[] = thread?.messages ?? [];

  const title =
    postTitleProp ??
    thread?.classPost?.title ??
    thread?.learningAssignmentClassPost?.title ??
    "";

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || messages.length === 0) {
      return;
    }

    const scrollToBottom = () => {
      container.scrollTop = container.scrollHeight;
    };

    scrollToBottom();

    // Re-scroll when child elements resize (e.g., images finishing load)
    const resizeObserver = new ResizeObserver(scrollToBottom);
    for (const child of Array.from(container.children)) {
      resizeObserver.observe(child);
    }

    return () => resizeObserver.disconnect();
  }, [messages.length]);

  return (
    <ThreadViewLayout
      section={section}
      leader={leader}
      postTitle={title}
      onBack={onBack}
      onClose={onClose}
    >
      <Box
        ref={scrollContainerRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          padding: "1.2rem",
        }}
      >
        {loading && messages.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          messages.map((message, index) => {
            const prev = messages[index - 1];
            const next = messages[index + 1];
            const showDateSeparator =
              !prev ||
              dayjs(message.sentAt).format("YYYY-MM-DD") !==
                dayjs(prev.sentAt).format("YYYY-MM-DD");
            const isFirstInGroup =
              showDateSeparator || !prev || !isSameGroup(prev, message);
            const isLastInGroup = !next || !isSameGroup(message, next);

            return (
              <React.Fragment key={message.uid}>
                {showDateSeparator && (
                  <DateSeparator
                    date={dayjs(message.sentAt).format("MMMM D, YYYY")}
                  />
                )}
                <MessageBubble
                  message={message}
                  teacher={leader}
                  learnerAvatar={learner.avatar}
                  showTimestamp={isFirstInGroup}
                  showAvatar={isFirstInGroup}
                  isLastInGroup={isLastInGroup}
                />
              </React.Fragment>
            );
          })
        )}
      </Box>

      <MessageInput
        sectionUid={section.uid}
        threadUid={threadUid}
        learnerUid={learner.uid}
        learnerName={learner.name ?? ""}
        onMessageSent={() => refetch()}
      />
    </ThreadViewLayout>
  );
}
